import type { ProfileSummary, ProjectSummary, SessionSummary } from "../types";

/**
 * `ok` takes new conversations; `broken` is a profile that fails to load right now; `missing` is
 * a profile that is gone from the registry entirely. The last two exist only to keep their old
 * sessions reachable — the sidebar must never silently swallow a conversation.
 */
export type GroupStatus = "ok" | "broken" | "missing";

/**
 * A session's label in the sidebar. One a schedule opened is marked, so it does not read as
 * something the person started; an anonymous one is marked because nothing in it is remembered.
 */
export function sessionTitle(session: Pick<SessionSummary, "title" | "createdBy" | "anonymous">): string {
	const title = session.title ?? "新对话";
	if (session.anonymous) return `◌ ${title}`;
	return session.createdBy === "schedule" ? `◷ ${title}` : title;
}

/** A session's sharing in the sidebar: whose it is when it is someone else's, with whom when it is mine. */
export function sharingLabel(session: Pick<SessionSummary, "owner" | "sharedWith">, me: string, nameOf: (userId: string) => string): string | null {
	if (session.owner !== me) return `来自 ${nameOf(session.owner)}`;
	return session.sharedWith.length > 0 ? `已共享给 ${session.sharedWith.map(nameOf).join("、")}` : null;
}

/** How many conversations a profile holds: inside its projects, loose, and branched off either. */
export function conversationCount(group: Pick<SessionGroup, "projects" | "sessions" | "branches">): number {
	let n = group.sessions.length + group.projects.reduce((acc, p) => acc + p.sessions.length, 0);
	for (const list of group.branches.values()) n += list.length;
	return n;
}

/** One project and the conversations filed under it. */
export interface ProjectGroup {
	project: ProjectSummary;
	sessions: SessionSummary[];
}

export interface SessionGroup {
	profile: string;
	status: GroupStatus;
	/** The loaded profile, for the header's description and meta line. Null unless `status` is "ok". */
	summary: ProfileSummary | null;
	/** The profile's projects, in the order they arrive, each with its own conversations. */
	projects: ProjectGroup[];
	/** Conversations filed under no project. */
	sessions: SessionSummary[];
	/** Branches by the session they were cut from, nested under it wherever it sits. */
	branches: Map<string, SessionSummary[]>;
}

/**
 * The sidebar's whole model: sessions filed under the profile that runs them, and inside a
 * profile under their project when they have one.
 *
 * A session only gets a title once the server has seen its first message, so `title === null`
 * marks one that was opened and abandoned, such as by a misclick. Those are dropped, with a
 * single exception: the session you are looking at right now stays, or a brand-new conversation
 * would vanish from the sidebar the instant you started it.
 *
 * Groups follow the order profiles arrive in rather than session recency. The sidebar is a
 * directory of what you can talk to, and a list that reshuffles as you work is hard to aim at.
 *
 * A session whose project is not in the list (archived, or another person's) is shown loose
 * rather than hidden: the project is the filing, not the conversation. A branch nests under its
 * parent by the same rule: a parent that is not in the list leaves the branch loose.
 */
export function groupSessions(input: {
	profiles: ProfileSummary[];
	broken: Array<{ name: string }>;
	sessions: SessionSummary[];
	projects?: ProjectSummary[];
	currentSessionId: string | null;
}): SessionGroup[] {
	const visible = input.sessions.filter((s) => s.title !== null || s.id === input.currentSessionId);

	const groups: SessionGroup[] = [
		...input.profiles.map((p): SessionGroup => ({ profile: p.name, status: "ok", summary: p, projects: [], sessions: [], branches: new Map() })),
		...input.broken.map((b): SessionGroup => ({ profile: b.name, status: "broken", summary: null, projects: [], sessions: [], branches: new Map() })),
	];
	const byProfile = new Map(groups.map((g) => [g.profile, g]));

	function groupFor(profile: string): SessionGroup {
		let group = byProfile.get(profile);
		if (!group) {
			// A profile deleted out from under its sessions. Appending keeps those conversations
			// openable instead of stranding them behind a filter they cannot see.
			group = { profile, status: "missing", summary: null, projects: [], sessions: [], branches: new Map() };
			byProfile.set(profile, group);
			groups.push(group);
		}
		return group;
	}

	const byProject = new Map<string, ProjectGroup>();
	for (const project of input.projects ?? []) {
		const pg: ProjectGroup = { project, sessions: [] };
		byProject.set(project.id, pg);
		groupFor(project.profile).projects.push(pg);
	}

	const visibleIds = new Set(visible.map((s) => s.id));
	for (const session of visible) {
		if (session.parentId && visibleIds.has(session.parentId)) {
			const branches = groupFor(session.profile).branches;
			branches.set(session.parentId, [...(branches.get(session.parentId) ?? []), session]);
			continue;
		}
		const pg = session.projectId ? byProject.get(session.projectId) : undefined;
		if (pg && pg.project.profile === session.profile) pg.sessions.push(session);
		else groupFor(session.profile).sessions.push(session);
	}

	return groups;
}
